"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  FileCheck, 
  HelpCircle, 
  Send, 
  Users, 
  CheckCircle2,
  AlertCircle,
  ChevronRight
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const steps = [
  {
    icon: <Users className="w-6 h-6" />,
    title: "Choose Your Intent",
    description: "Whether you're a founder seeking clarity or a student exploring careers, pick a trip that aligns with your current goals."
  },
  {
    icon: <FileCheck className="w-6 h-6" />,
    title: "Fill the Application",
    description: "Tell us about yourself, your background, and why you want to join this specific cohort. We look for intent more than credentials."
  },
  {
    icon: <HelpCircle className="w-6 h-6" />,
    title: "Shortlisting Call",
    description: "If your profile fits, we'll schedule a quick 10-15 min chat to ensure the group energy is right for everyone."
  },
  {
    icon: <Send className="w-6 h-6" />,
    title: "Reserve Your Spot",
    description: "Once selected, complete your registration and prepare for a journey that will change your perspective."
  }
];

const TripHelpPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero Section */}
      <section className="bg-gray-50 py-20 px-6 border-b border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block bg-[#FFAE00]/10 text-[#FFAE00] px-4 py-2 rounded-full font-bold text-sm mb-6"
          >
            APPLICATION GUIDE
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bricolage font-bold text-[#063258] mb-6">
            Everything You Need to Know About Applying
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Our trips are curated for specific outcomes. Here&apos;s how we select our tribe and how you can increase your chances of being a part of it.
          </p>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bricolage font-bold text-center mb-16 underline decoration-[#FFAE00] underline-offset-8">
          The Application Process
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <div key={idx} className="relative">
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-xl shadow-gray-100/50 h-full flex flex-col items-center text-center">
                <div className="bg-[#063258] text-[#FFAE00] w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
              {idx < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ChevronRight className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Tips for Selection */}
      <section className="py-20 px-6 bg-[#063258] text-white">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bricolage font-bold mb-8">Tips for a Winning Application</h2>
            <div className="space-y-6 text-lg">
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#FFAE00] shrink-0" />
                <p><span className="font-bold underline">Be Authentic:</span> We care about your real story, not a resume version of it.</p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#FFAE00] shrink-0" />
                <p><span className="font-bold underline">Define Your &quot;Why&quot;:</span> Clearly state what outcome you&apos;re looking for from this trip.</p>
              </div>
              <div className="flex gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#FFAE00] shrink-0" />
                <p><span className="font-bold underline">Show Contribution:</span> Tell us how you&apos;ll add value to the other participants in the mix.</p>
              </div>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20">
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <AlertCircle className="text-[#FFAE00]" />
              Common Mistakes
            </h3>
            <ul className="space-y-4 opacity-80">
              <li>• Providing one-word answers in the application form.</li>
              <li>• Applying for multiple trips with the same generic response.</li>
              <li>• Not being available for the shortlisting interview call.</li>
              <li>• Treating it like a vacation instead of a growth experience.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQs Highlight */}
      <section className="py-20 px-6 max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bricolage font-bold mb-6">Still have questions?</h2>
        <p className="text-gray-600 mb-10 text-lg">
          Check out our comprehensive FAQ page or reach out to our team via WhatsApp for instant clarifications.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link href="/faqs">
            <Button variant="outline" className="border-[#063258] text-[#063258] hover:bg-gray-50 px-8 py-6 rounded-xl font-bold text-lg w-full sm:w-auto">
              View All FAQs
            </Button>
          </Link>
          <Link href="https://wa.me/917982579691" target="_blank">
            <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 rounded-xl font-bold text-lg w-full sm:w-auto">
              Message on WhatsApp
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TripHelpPage;
